import { useGetBreederApplications } from '@application/api/account/applications/useGetBreederApplications';
import { MtApplicationsOwnModalController } from '@application/components';
import { ApplicationType } from '@application/types';
import { useModal, useRouter } from '@application/utils';
import type { ActionsPopperData, ActionsPopperItem } from '@presentation/components';
import { ActionsPopper, MtApplicationsBreederRejectModal } from '@presentation/components';
import { MtApplicationsPage } from '@presentation/pages';
import { useTranslation } from '@presentation/utils';
import { FormikProvider } from 'formik';
import { useMemo, useState } from 'react';
import { useApplicationsShared } from '../../../../utils/hooks/useApplicationsShared';
import { useApplicationMessageModal } from '../../ApplicationMessageModalController';
import { useSharedApplicationBreeder } from '../MtApplicationBreederPageController/useSharedApplicationBreeder';
import { useGetConversations } from '../MtMessagesPageController/api/useGetConversations';
import type { MtApplicationsBreederPageControllerProps } from './types';
import { useFormatApplications } from './useFormatApplications';

export * from './types';

export const MtApplicationsBreederPageController = (
  props: MtApplicationsBreederPageControllerProps,
) => {
  const { type } = props;
  const [showArchived, setShowArchived] = useState(false);

  const { translate } = useTranslation();
  const { navigateToRoute } = useRouter();

  const { applications, applicationsBreeder, applicationsLoading } = useGetBreederApplications({
    type,
    archived: showArchived,
  });
  const { conversations } = useGetConversations();

  const { show, setAccountId } = useApplicationMessageModal();
  const { show: showOwnModal, modal: ownModal } = useModal();

  const {
    popperItems: sharedPopperItems,
    selectedApplication,
    setSelectedApplicationId,
    popper,
  } = useApplicationsShared({
    applications,
    type,
  });

  const {
    deleteModalForm,
    deleteModalProps,
    showDeleteModal,
    shouldShowOwnAction,
    shouldShowRefuseAction,
    shouldShowMessagesAction,
  } = useSharedApplicationBreeder(selectedApplication?.id);

  const onActionsClick = (application: any) => (event: any) => {
    popper.setTriggerElement(event.target as any);
    setSelectedApplicationId(application.id);
    popper.toggle();
  };

  const { formattedApplicationsCards } = useFormatApplications({
    applications: applications.filter((application) => application.seenByBreeder),
    onActionsClick,
    type,
  });

  const { formattedApplicationsCards: formattedNewApplicationsCards } = useFormatApplications({
    applications: applications.filter((application) => !application.seenByBreeder),
    onActionsClick,
    type,
  });

  const otherAccountId: number = useMemo(() => {
    return selectedApplication?.account?.id ?? selectedApplication?.dogBreeder?.id;
  }, [selectedApplication?.account?.id, selectedApplication?.dogBreeder?.id]);

  const existingConversationId: number | undefined = useMemo(() => {
    return conversations.find((x) => x.otherAccountId === otherAccountId)?.id;
  }, [conversations, otherAccountId]);

  const popperItems = useMemo((): ActionsPopperData['items'] => {
    const { status } = selectedApplication ?? {};
    return [
      ...(shouldShowOwnAction(status)
        ? [
            {
              label: translate('common.buttons.own'),
              iconName: 'dog',
              onClick: () => {
                popper.close();
                showOwnModal();
              },
            } as ActionsPopperItem,
          ]
        : []),

      ...(shouldShowMessagesAction(selectedApplication?.status)
        ? [
            {
              label: translate('common.buttons.messages'),
              iconName: 'comments',
              onClick: () => {
                popper.close();
                if (!selectedApplication?.id || !otherAccountId) throw new Error();
                if (existingConversationId) {
                  navigateToRoute('mtMessages', { params: [otherAccountId.toString()] });
                } else {
                  setAccountId(otherAccountId);
                  show();
                }
              },
            } as ActionsPopperItem,
          ]
        : []),
      ...sharedPopperItems,
      ...(shouldShowRefuseAction(status)
        ? [
            {
              label: translate('common.buttons.reject'),
              iconName: 'times',
              onClick: () => {
                popper.close();
                showDeleteModal();
              },
            } as ActionsPopperItem,
          ]
        : []),
    ];
  }, [
    existingConversationId,
    navigateToRoute,
    otherAccountId,
    popper,
    selectedApplication,
    setAccountId,
    sharedPopperItems,
    shouldShowMessagesAction,
    shouldShowOwnAction,
    shouldShowRefuseAction,
    show,
    showDeleteModal,
    showOwnModal,
    translate,
  ]);

  return (
    <>
      <MtApplicationsPage
        title={translate(`mt.breeder.applications.titles.${type}`)}
        emptyTitle={translate(`mt.applications.empty.titles.${type}`)}
        loading={applicationsLoading}
        topCards={formattedNewApplicationsCards}
        cards={formattedApplicationsCards}
        columnTitles={[
          translate('glossary.name'),
          translate('glossary.breed'),
          translate('glossary.status'),
          translate(`glossary.${type === ApplicationType.OWNERS ? 'birthdate' : 'createdAt'}`),
        ]}
        personType="breeder"
        showArchived={showArchived}
        onToggleShowArchived={type === ApplicationType.APPLICATIONS ? setShowArchived : null}
      />
      <ActionsPopper popper={popper.popper} items={popperItems} />
      <FormikProvider value={deleteModalForm}>
        <MtApplicationsBreederRejectModal {...deleteModalProps} />
      </FormikProvider>
      <MtApplicationsOwnModalController
        modal={ownModal}
        applicationId={selectedApplication?.id}
        breeder={applicationsBreeder}
        onRedirect={() => navigateToRoute('mtOwners')}
      />
    </>
  );
};
